import { isPlatformServer } from '@angular/common';
import { Component, effect, inject, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { catchError, map, of } from 'rxjs';
import { RouterLink } from '@angular/router';
import { buildPageTitle } from '../../../shared/constants/site-meta';
import type { BlogListingPageData } from '../../../shared/models/blog.models';
import { BlogsService } from '../../../shared/services/blogs.service';
import { MarketingBlogCardComponent } from '../../components/marketing-blog-card.component';
import { MarketingFooterComponent } from '../../components/marketing-footer.component';
import { MarketingHeaderComponent } from '../../components/marketing-header.component';
import { MarketingSectionHeadingComponent } from '../../components/marketing-section-heading.component';

export type BlogsPageLoadState =
  | { kind: 'pending' }
  | { kind: 'error' }
  | { kind: 'ok'; page: BlogListingPageData };

@Component({
  selector: 'app-marketing-blogs-listing-page',
  standalone: true,
  imports: [
    RouterLink,
    MarketingHeaderComponent,
    MarketingSectionHeadingComponent,
    MarketingBlogCardComponent,
    MarketingFooterComponent,
  ],
  templateUrl: './blogs-listing-page.component.html',
})
export class MarketingBlogsListingPageComponent {
  private readonly blogs = inject(BlogsService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly platformId = inject(PLATFORM_ID);

  readonly pageState = toSignal(
    this.blogs.listPublic(1, 12).pipe(
      map((page) => ({ kind: 'ok' as const, page })),
      catchError(() =>
        isPlatformServer(this.platformId)
          ? of({ kind: 'pending' as const })
          : of({ kind: 'error' as const }),
      ),
    ),
    { initialValue: { kind: 'pending' as const } satisfies BlogsPageLoadState },
  );

  constructor() {
    effect(() => {
      const s = this.pageState();
      if (s.kind === 'ok') this.applySeo(s.page);
    });
  }

  private applySeo(page: BlogListingPageData): void {
    this.title.setTitle(buildPageTitle(page.seo.title));
    if (page.seo.description) this.meta.updateTag({ name: 'description', content: page.seo.description });
  }
}
